import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Landmark, Undo2, Loader2, AlertTriangle, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '../ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Batches every documentId lookup rendered in the same tick into ONE request (list pages render 100+ chips).
const _pending = new Map();
let _flushTimer = null;
const _cache = new Map();
const locateByDocument = (documentId) => {
  if (_cache.has(documentId)) return Promise.resolve(_cache.get(documentId));
  return new Promise((resolve) => {
    if (!_pending.has(documentId)) _pending.set(documentId, []);
    _pending.get(documentId).push(resolve);
    if (_flushTimer) return;
    _flushTimer = setTimeout(async () => {
      const batch = new Map(_pending); _pending.clear(); _flushTimer = null;
      const ids = Array.from(batch.keys());
      let map = {};
      try {
        for (let i = 0; i < ids.length; i += 200) {
          const r = await axios.post(`${API}/bank-feeds/locate-by-documents`, { ids: ids.slice(i, i + 200) });
          Object.assign(map, r.data?.rows_by_document || {});
        }
      } catch { map = {}; }
      ids.forEach((id) => { const rows = map[id] || []; _cache.set(id, rows); batch.get(id).forEach((res) => res(rows)); });
      setTimeout(() => ids.forEach((id) => _cache.delete(id)), 30000);
    }, 40);
  });
};

const BUCKET = { pending: 'Review Needed', processed: 'In Books', duplicates: 'Duplicates', failed: 'Failed', awaiting_bank: 'Awaiting bank' };

/**
 * Everywhere a bank-feed-linked thing surfaces: "View in Bank Feeds" deep link(s) + optional
 * "Undo bank feed match" that runs the shared undo engine (group-confirm + settlement guards included).
 * Resolve by `documentId` (any doc/special item/journal) or pass `transactionId`/`transactionIds` directly.
 */
export const BankTxActions = ({ documentId, transactionId, transactionIds, allowUndo = false, compact = false, onUndone, className = '' }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState(() => (transactionIds || (transactionId ? [transactionId] : [])).map((id) => ({ transaction_id: id })));
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    if (transactionId || transactionIds?.length || !documentId) return;
    let alive = true;
    locateByDocument(documentId).then((rows) => { if (alive) setRows(rows); });
    return () => { alive = false; };
  }, [documentId, transactionId, transactionIds]);

  if (!rows.length) return null;

  const undo = async (txId, confirmGroup = false) => {
    setBusy(txId);
    try {
      const r = await axios.post(`${API}/bank-feeds/undo-classification/${txId}`, { reason: 'Undone from document', cascade_reverse_downstream: true, confirm_group: confirmGroup });
      const n = r.data?.group_results?.length || r.data?.undone_count || 1;
      toast.success(n > 1 ? `${n} bank lines moved back to Review Needed; their journals and matches were reversed.` : 'Bank line moved back to Review Needed; its journals and match were reversed.');
      setConfirm(null);
      onUndone?.(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.code === 'requires_group_confirm') { setConfirm({ txId, members: d.members || [], message: d.message }); return; }
      toast.error(typeof d === 'string' ? d : d?.message || 'Could not undo this bank transaction');
    } finally { setBusy(null); }
  };

  const linkBtn = (r, i, label) => (
    <button type="button" onClick={(e) => { e.stopPropagation(); navigate(`/bank-feeds?tx=${r.transaction_id}`); }}
      className={`inline-flex items-center gap-1 font-medium text-[#108981] underline underline-offset-2 hover:text-[#0b6b65] ${compact ? 'text-[11px]' : 'text-xs'}`}
      data-testid={`bank-tx-deeplink-${r.transaction_id}`} title={r.description ? `${r.date || ''} · ${r.description}` : 'Open this transaction in Bank Feeds'}>
      <Landmark className="w-3.5 h-3.5" />{label}
      {r.bucket && !compact && <span className="text-slate-500 font-normal">· {BUCKET[r.bucket] || r.bucket}{r.date ? ` · ${r.date}` : ''}</span>}
    </button>
  );
  const undoBtn = (r) => allowUndo && r.bucket !== 'pending' && (
    <button type="button" disabled={busy === r.transaction_id} onClick={(e) => { e.stopPropagation(); undo(r.transaction_id); }}
      className={`inline-flex items-center gap-1 font-medium text-orange-700 hover:text-orange-900 underline underline-offset-2 disabled:opacity-50 ${compact ? 'text-[11px]' : 'text-xs'}`}
      data-testid={`bank-tx-undo-${r.transaction_id}`} title="Reverse this bank match: journals reversed, row returns to Review Needed">
      {busy === r.transaction_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />}Undo bank match
    </button>
  );

  return (
    <span className={`inline-flex flex-wrap items-center gap-x-3 gap-y-1 ${className}`} data-testid="bank-tx-actions" data-no-row-click="true">
      {rows.length === 1 ? (
        <span className="inline-flex items-center gap-1.5">{linkBtn(rows[0], 0, 'View in Bank Feeds')}{undoBtn(rows[0])}</span>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" onClick={(e) => e.stopPropagation()}
              className={`inline-flex items-center gap-1 font-medium text-[#108981] underline underline-offset-2 hover:text-[#0b6b65] ${compact ? 'text-[11px]' : 'text-xs'}`}
              data-testid="bank-tx-lines-trigger" title="This item is linked to several bank feed lines">
              <Landmark className="w-3.5 h-3.5" />{rows.length} bank feed lines<ChevronDown className="w-3 h-3" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-80 max-w-[90vw]" data-testid="bank-tx-lines-menu" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuLabel className="text-[11px] text-slate-500 font-normal">Open in Bank Feeds</DropdownMenuLabel>
            {rows.map((r) => (
              <DropdownMenuItem key={r.transaction_id} className="flex items-center gap-2 cursor-pointer" data-testid={`bank-tx-deeplink-${r.transaction_id}`}
                onSelect={() => navigate(`/bank-feeds?tx=${r.transaction_id}`)}>
                <Landmark className="w-3.5 h-3.5 text-[#108981] shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="block text-xs truncate">{r.description || 'Bank transaction'}</span>
                  <span className="block text-[10px] text-slate-500">{r.date || ''}{r.bucket ? ` · ${BUCKET[r.bucket] || r.bucket}` : ''}</span>
                </span>
                {r.amount != null && <span className="text-xs font-mono tabular-nums shrink-0">{Number(r.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>}
                {allowUndo && r.bucket !== 'pending' && (
                  <button type="button" disabled={busy === r.transaction_id} title="Undo this bank match"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); undo(r.transaction_id); }}
                    className="p-1 rounded text-orange-700 hover:bg-orange-50 disabled:opacity-50 shrink-0" data-testid={`bank-tx-undo-${r.transaction_id}`}>
                    {busy === r.transaction_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />}
                  </button>
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <Dialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <DialogContent data-testid="bank-tx-group-undo-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-600" />This match used {confirm?.members?.length} bank lines</DialogTitle>
            <DialogDescription>{confirm?.message} All of them go back to Review Needed and every journal they posted is reversed.</DialogDescription>
          </DialogHeader>
          <ul className="text-xs space-y-1 max-h-48 overflow-auto rounded-lg border p-2 bg-slate-50">
            {(confirm?.members || []).map((m) => (
              <li key={m.id} className="flex justify-between gap-2"><span className="truncate">{m.date} · {m.description}</span><span className="font-mono tabular-nums shrink-0">{Number(m.amount).toLocaleString()}</span></li>
            ))}
          </ul>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button variant="destructive" disabled={!!busy} onClick={() => undo(confirm.txId, true)} data-testid="bank-tx-group-undo-confirm">Undo all {confirm?.members?.length} lines</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </span>
  );
};

export default BankTxActions;
